import React, {FC, ReactElement, ReactNode, useContext, useRef, useState} from 'react';
import classNames from 'classnames';
import {useRouter} from 'next/router';
import {useClickOutside} from 'core/hooks/useClickOutside';
import Icon from 'components/Icon';
import {Typography} from 'components/Typography';
import style from './style.module.scss';
import {EventsListContext} from 'modules/events/context';

interface IProps {
    title: string;
    selectedLabels?: string[];
    code: string;
    isDisabled?: boolean;
    children: ReactNode;
}

export const EventFilter: FC<IProps> = ({children, title, selectedLabels = [], code, isDisabled = false}) => {
    const {isLoading} = useContext(EventsListContext);

    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const router = useRouter();

    const toggleDropdown = () => setIsOpen((prev) => !prev);

    const handleResetFilter = async (e: React.MouseEvent) => {
        e.stopPropagation();
        const {pathname, query} = router;
        const {page, ...restParams} = query;

        // eslint-disable-next-line @typescript-eslint/naming-convention
        const {[code]: _, ...newQuery} = restParams;

        try {
            await router.push({pathname, query: newQuery}, undefined, {shallow: true});
        } catch {}
    };

    const isActive = 0 < selectedLabels.length;
    const displayTitle = isActive ? selectedLabels.join(', ') : title;

    const buttonIcon = isActive ? (
        <Icon className={style.closeIcon} icon={'close'} />
    ) : (
        <Icon className={style.arrowIcon} icon={'arrow-collapse'} />
    );

    const handleClosePopup = () => {
        setIsOpen(false);
    };

    useClickOutside([ref], handleClosePopup);

    return (
        <div
            className={classNames(style.main, {
                [style.isOpen]: isOpen,
                [style.isActive]: isActive,
                [style.isDisabled]: isLoading || isDisabled,
            })}
            ref={ref}
        >
            <button
                className={style.button}
                onClick={toggleDropdown}
                type={'button'}
                title={displayTitle}
                aria-expanded={isOpen}
            >
                <Typography variant={'label-14-m'} className={style.title}>
                    {displayTitle}
                </Typography>

                <div className={style.icon} onClick={isActive ? handleResetFilter : undefined}>
                    {buttonIcon}
                </div>
            </button>

            {isOpen && (
                <>
                    <div className={style.overlay} onClick={handleClosePopup} />
                    <div className={style.popup}>
                        <div className={classNames(style.icon, style.closeIcon)} onClick={handleClosePopup}>
                            <Icon icon={'close'} />
                        </div>
                        {React.cloneElement(children as ReactElement, {
                            onClose: handleClosePopup,
                        })}
                    </div>
                </>
            )}
        </div>
    );
};
